import { useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import type { CarReservation } from "../types";
import { formatDate } from "@/utils/formatDate";

interface Props {
  reservations: CarReservation[];
  currentUserId: string;
  isManager?: boolean;
  onSelectRange: (from: string, to: string) => void;
  onDeleteReservation: (reservation: CarReservation) => void;
  carIsElsewhere?: boolean;
  carLocationLabel?: string;
}

const MONTHS_PL = [
  "Styczeń", "Luty", "Marzec", "Kwiecień", "Maj", "Czerwiec",
  "Lipiec", "Sierpień", "Wrzesień", "Październik", "Listopad", "Grudzień",
];
const DAYS_PL = ["Pn", "Wt", "Śr", "Cz", "Pt", "So", "Nd"];

// Use local getters instead of toISOString() — toISOString() returns UTC, so in UTC+2
// new Date(2026, 4, 20) (local midnight) becomes "2026-05-19" (previous day in UTC),
// causing every calendar cell to appear one column to the right.
function toISO(d: Date) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function daysInMonth(year: number, month: number) {
  return new Date(year, month + 1, 0).getDate();
}

// Monday-based week offset (0=Mon, 6=Sun)
function weekOffset(year: number, month: number) {
  const dow = new Date(year, month, 1).getDay();
  return (dow + 6) % 7; // 0=Mon
}

export function ReservationCalendar({ reservations, currentUserId, isManager, onSelectRange, onDeleteReservation, carIsElsewhere, carLocationLabel }: Props) {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());
  const [selectStart, setSelectStart] = useState<string | null>(null);
  const [hoverDate, setHoverDate] = useState<string | null>(null);

  const prevMonth = () => {
    if (month === 0) { setMonth(11); setYear(y => y - 1); }
    else setMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (month === 11) { setMonth(0); setYear(y => y + 1); }
    else setMonth(m => m + 1);
  };

  const days = daysInMonth(year, month);
  const offset = weekOffset(year, month);

  // Build set of reserved date ranges for quick lookup
  const reservedMap: Map<string, CarReservation> = new Map();
  for (const res of reservations) {
    const from = new Date(res.date_from);
    const to = new Date(res.date_to);
    const cur = new Date(from);
    while (cur <= to) {
      reservedMap.set(toISO(cur), res);
      cur.setDate(cur.getDate() + 1);
    }
  }

  const isReserved = (dateStr: string) => reservedMap.has(dateStr);
  const isToday = (dateStr: string) => dateStr === toISO(today);
  const isPast = (dateStr: string) => dateStr < toISO(today);

  const getSelectionClass = (dateStr: string) => {
    if (!selectStart) return "";
    const anchor = selectStart;
    const hover = hoverDate ?? anchor;
    const [lo, hi] = anchor <= hover ? [anchor, hover] : [hover, anchor];
    if (dateStr >= lo && dateStr <= hi) return "selected";
    return "";
  };

  const handleDayClick = (dateStr: string) => {
    if (isPast(dateStr) || isReserved(dateStr)) return;
    if (!selectStart) {
      setSelectStart(dateStr);
    } else {
      const [from, to] = selectStart <= dateStr ? [selectStart, dateStr] : [dateStr, selectStart];
      // Check no reserved day in between
      const cur = new Date(from);
      const end = new Date(to);
      let hasConflict = false;
      while (cur <= end) {
        if (reservedMap.has(toISO(cur))) { hasConflict = true; break; }
        cur.setDate(cur.getDate() + 1);
      }
      if (hasConflict) {
        setSelectStart(dateStr); // restart selection
        return;
      }
      onSelectRange(from, to);
      setSelectStart(null);
      setHoverDate(null);
    }
  };

  const cells: (string | null)[] = [
    ...Array(offset).fill(null),
    ...Array.from({ length: days }, (_, i) => {
      const d = new Date(year, month, i + 1);
      return toISO(d);
    }),
  ];
  // pad to full weeks
  while (cells.length % 7 !== 0) cells.push(null);

  return (
    <div className="select-none">
      {/* Warning: car is not in Wrocław */}
      {carIsElsewhere && (
        <div className="mb-3 rounded-md bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 px-3 py-2 text-sm text-red-700 dark:text-red-300">
          Uwaga: samochód aktualnie jest na <strong>{carLocationLabel ?? "innej lokalizacji"}</strong>. Możesz zarezerwować termin, ale dostępność zależy od powrotu pojazdu.
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <button onClick={prevMonth} className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700">
          <ChevronLeft size={16} />
        </button>
        <span className="font-semibold text-sm text-gray-800 dark:text-gray-200">
          {MONTHS_PL[month]} {year}
        </span>
        <button onClick={nextMonth} className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700">
          <ChevronRight size={16} />
        </button>
      </div>

      {/* Day labels */}
      <div className="grid grid-cols-7 mb-1">
        {DAYS_PL.map((d) => (
          <div key={d} className="text-center text-xs font-medium text-gray-400 dark:text-gray-500 py-1">
            {d}
          </div>
        ))}
      </div>

      {/* Days grid */}
      <div className="grid grid-cols-7 gap-px">
        {cells.map((dateStr, i) => {
          if (!dateStr) return <div key={i} />;

          const reserved = isReserved(dateStr);
          const past = isPast(dateStr);
          const todayMark = isToday(dateStr);
          const selClass = getSelectionClass(dateStr);
          const isStart = selectStart === dateStr;
          const reservation = reservedMap.get(dateStr);
          const isOwnReservation = reservation?.reserved_by === currentUserId;
          const canCancel = isOwnReservation || !!isManager;

          return (
            <div
              key={dateStr}
              onMouseEnter={() => selectStart && setHoverDate(dateStr)}
              onMouseLeave={() => selectStart && setHoverDate(null)}
              onClick={() => handleDayClick(dateStr)}
              title={
                reserved
                  ? `${reservation?.reserved_by_name} (${formatDate(reservation?.date_from)} – ${formatDate(reservation?.date_to)})`
                  : undefined
              }
              className={[
                "relative flex flex-col items-center justify-start py-1 rounded cursor-pointer transition-colors min-h-[36px]",
                past && !reserved ? "opacity-30 cursor-default" : "",
                reserved
                  ? isOwnReservation
                    ? "bg-[var(--color-primary-dim)] dark:bg-[var(--color-primary-hover)] cursor-pointer"
                    : canCancel
                    ? "bg-red-100 dark:bg-red-900/30 cursor-pointer"
                    : "bg-red-100 dark:bg-red-900/30 cursor-not-allowed"
                  : selClass === "selected"
                  ? "bg-[var(--color-primary-glow)] dark:bg-[var(--color-primary-hover)]/60"
                  : isStart
                  ? "bg-[var(--color-primary)] text-white"
                  : "hover:bg-gray-100 dark:hover:bg-gray-700",
                todayMark ? "ring-1 ring-[var(--color-primary)]" : "",
              ]
                .filter(Boolean)
                .join(" ")}
            >
              <span
                className={[
                  "text-xs font-medium",
                  reserved
                    ? isOwnReservation
                      ? "text-[var(--color-primary)] dark:text-[var(--color-primary)]"
                      : "text-red-700 dark:text-red-300"
                    : selClass === "selected" || isStart
                    ? "text-[var(--color-primary)] dark:text-[var(--color-primary)]"
                    : "text-gray-700 dark:text-gray-300",
                ]
                  .filter(Boolean)
                  .join(" ")}
              >
                {new Date(dateStr).getDate()}
              </span>

              {/* Cancel button — own reservation or manager */}
              {reserved && canCancel && reservation && (
                <button
                  onClick={(e) => { e.stopPropagation(); onDeleteReservation(reservation); }}
                  className={`absolute top-0.5 right-0.5 text-[9px] leading-none font-bold hover:text-red-500 ${isOwnReservation ? "text-[var(--color-primary)]" : "text-red-400"}`}
                  title="Anuluj rezerwację"
                >
                  ×
                </button>
              )}
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div className="mt-3 flex flex-wrap gap-3 text-xs text-gray-500 dark:text-gray-400">
        <span className="flex items-center gap-1.5">
          <span className="w-3.5 h-3.5 rounded bg-[var(--color-primary)] inline-block" /> Twoja rezerwacja (kliknij × aby anulować)
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-3.5 h-3.5 rounded bg-red-400 dark:bg-red-500 inline-block" /> Zajęte
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-3.5 h-3.5 rounded bg-sky-200 dark:bg-sky-700 inline-block" /> Wybierany zakres
        </span>
      </div>

      {selectStart && (
        <p className="mt-2 text-xs text-[var(--color-primary)] dark:text-[var(--color-primary)] font-medium">
          Zaznaczono: {formatDate(selectStart)} — kliknij datę końcową lub <button className="underline" onClick={() => { setSelectStart(null); setHoverDate(null); }}>anuluj</button>
        </p>
      )}
    </div>
  );
}
